import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import http from "../../../api/apiClient";
import { AuthContext } from "../../../AuthProvider";
import PageTemplate from "../../../components/PageTemplate";
import SlidePageTransition from "../../../components/transitions/SlidePageTransition";
import { useTranslation } from "react-i18next";
import ErrorPage from "../../../components/ErrorPage";
import Spinner from "../../../components/Spinner";
import { Switch } from "@headlessui/react";
import { toIsoDate, parseToDate } from "../../../utils/dateUtils";
import { translateClassName } from "../../../utils/classroomUtils";
import Modal from "../../../components/Modal";
import ChildPrivacyForm from "./components/ChildPrivacyForm";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaChild,
  FaCog,
  FaInfoCircle,
  FaSearch,
  FaSave,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";
import {
  getCachedChild,
  getCachedChildDetails,
  getCachedClassrooms,
  getCachedFamilies,
  hasClassroomsCache,
  hasFamiliesCache,
  loadClassroomsOverview,
  loadFamiliesOverview,
  setCachedChildDetails,
  upsertCachedChild,
} from "../../familyOverviewCache";
import { movePrimaryFamily, replacePrimaryFamily } from "./childFamilyMoveUtils";

import BackButton, { useLeaveForm } from "../../../components/navigation/BackButton";
import { useFormDirtyState, useUnsavedChangesGuard } from "../../../components/navigation/UnsavedChangesGuard";
const childEditTabOrder = ["general", "classrooms", "management", "privacy"];

const familyParentNames = (family) => [
  [family?.parent1FirstName, family?.parent1LastName].filter(Boolean).join(" ").trim(),
  [family?.parent2FirstName, family?.parent2LastName].filter(Boolean).join(" ").trim(),
].filter(Boolean);

const familySearchText = (family) => [
  family?.familyName,
  ...familyParentNames(family),
  family?.parent1Email,
  family?.parent2Email,
].filter(Boolean).join(" ").toLocaleLowerCase();

/* -------------------------------------------------------------------------- */
/* 🟢 ChildEdit component                                                     */
/* -------------------------------------------------------------------------- */
const ChildEdit = () => {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const cachedDetails = id ? getCachedChildDetails(id) : null;
  const cachedChild = id ? cachedDetails?.child ?? getCachedChild(id) : null;
  const cachedFamilies = useMemo(() => getCachedFamilies(), []);
  const cachedClassrooms = useMemo(() => getCachedClassrooms(), []);

  const mapChildToFormState = useCallback((childData) => {
    if (!childData) {
      return {
        firstName: "",
        lastName: "",
        bsn: "",
        gender: "",
        dateBirth: "",
        dateRegistration: new Date().toISOString().split("T")[0],
        withdrawnAt: "",
        deceasedAt: "",
        committed: false,
        waiting: true,
        families: [],
        classrooms: [],
      };
    }
    const normalizedFamilyIds = childData.families?.map((f) => String(f.id)) || [];
    return {
      ...childData,
      dateBirth: toIsoDate(childData.dateBirth),
      dateRegistration: toIsoDate(childData.dateRegistration),
      withdrawnAt: toIsoDate(childData.withdrawnAt),
      deceasedAt: toIsoDate(childData.deceasedAt),
      families: normalizedFamilyIds,
      committed: !!childData.committed,
      waiting: childData.waiting ?? true,
    };
  }, []);

  const [originalChild, setOriginalChild] = useState(cachedChild);

  const [activeSection, setActiveSection] = useState("kindergarten"); // hoofdsecties
  const [activeTab, setActiveTab] = useState("general"); // tabs
  const [tabSlideDirection, setTabSlideDirection] = useState("forward");

  const [child, setChild] = useState(() => mapChildToFormState(cachedChild));

  const [families, setFamilies] = useState(() => cachedFamilies ?? []);
  const [selectedFamilies, setSelectedFamilies] = useState(() => cachedChild?.families?.map((f) => String(f.id)) || []);
  const [primaryFamilyId, setPrimaryFamilyId] = useState(() =>
    String(cachedChild?.familyId || cachedChild?.families?.[0]?.id || "")
  );
  const [familySearchTerm, setFamilySearchTerm] = useState("");
  const [classrooms, setClassrooms] = useState(() => cachedClassrooms ?? []);
  const [selectedClassrooms, setSelectedClassrooms] = useState(() => cachedChild?.classroomAssignments ?? {});
  const [assignmentsToDelete, setAssignmentsToDelete] = useState([]);
  const [unenrollReference, setUnenrollReference] = useState(null);
  const [showReferenceModal, setShowReferenceModal] = useState(false);

  // 🔵 Privacy: state
  const [privacy, setPrivacy] = useState(() => cachedDetails?.privacy ?? null);
  const [privacyLoading, setPrivacyLoading] = useState(() => !cachedDetails);
  const [privacyError, setPrivacyError] = useState(null);

  const [loading, setLoading] = useState(() => !cachedChild || !hasFamiliesCache() || !hasClassroomsCache());
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmWithoutBsn, setConfirmWithoutBsn] = useState(false);
  const [error, setError] = useState(null);

  // ref naar PrivacyForm
  const privacyRef = useRef(null);
  const dataLoadIdRef = useRef(null);
  const formDirtyRef = useRef(false);

  // Unsaved changes: the child's fields, families and classrooms, plus the privacy tab's own form.
  const [privacyDirty, setPrivacyDirty] = useState(false);
  const { isDirty: formDirty, markClean } = useFormDirtyState({ child, selectedFamilies, primaryFamilyId, selectedClassrooms });
  const initialClassroomsRef = useRef(selectedClassrooms);
  const { leaveWithoutWarning } = useUnsavedChangesGuard(formDirty || privacyDirty);

  // Helpers
  const isFuture = (d) => d && new Date(d) > new Date();
  const isLifecycleField = (name) => name === "withdrawnAt" || name === "deceasedAt";

  const getKindergartenKeys = () => {
    const keysFromData = Object.keys(selectedClassrooms || {}).filter((k) =>
      k.startsWith("kindergarten")
    );
    const maxIndex = Math.max(
      3,
      ...keysFromData
        .map((k) => parseInt(k.replace("kindergarten", ""), 10))
        .filter((n) => !isNaN(n))
    );
    return Array.from({ length: maxIndex }, (_, i) => `kindergarten${i + 1}`);
  };

  // 🔵 Privacy: API helpers
  const fetchPrivacy = async (childId) => {
    try {
      setPrivacyError(null);
      setPrivacyLoading(true);
      const { data } = await http.get(`/children/${childId}/privacy-consent`);
      setPrivacy(data ?? null); // als er nog niks is aangemaakt
      const existing = getCachedChildDetails(childId);
      setCachedChildDetails(childId, {
        child: existing?.child ?? getCachedChild(childId),
        classroomHistory: existing?.classroomHistory ?? [],
        privacy: data ?? null,
      });
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 204) {
        setPrivacy(null);
      } else {
        setPrivacyError(e?.response?.data?.message || e.message || "Failed to load privacy");
      }
    } finally {
      setPrivacyLoading(false);
    }
  };

  const savePrivacy = async (payload) => {
    const { data } = await http.put(`/children/${id}/privacy-consent`, payload);
    setPrivacy(data);
    const existing = getCachedChildDetails(id);
    setCachedChildDetails(id, {
      child: existing?.child ?? getCachedChild(id),
      classroomHistory: existing?.classroomHistory ?? [],
      privacy: data,
    });
  };

  // Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!keycloak?.token) {
          setError(t("common.auth_required"));
          setLoading(false);
          return;
        }

        const [childRes, familiesData, classroomsData] = await Promise.all([
          http.get(`/children/${id}`),
          loadFamiliesOverview(),
          loadClassroomsOverview(),
        ]);

        const childData = childRes.data;
        setOriginalChild(childData);
        upsertCachedChild(childData);

        const normalizedFamilyIds = childData.families?.map((f) => String(f.id)) || [];

        const loadedChild = mapChildToFormState(childData);
        const loadedPrimaryFamilyId = String(childData.familyId || childData.families?.[0]?.id || "");
        const hasClassroomAssignments = childData.classroomAssignments && typeof childData.classroomAssignments === "object";
        if (!formDirtyRef.current) {
          setChild(loadedChild);
          markClean({
            child: loadedChild,
            selectedFamilies: normalizedFamilyIds,
            primaryFamilyId: loadedPrimaryFamilyId,
            // Without valid assignments from the server the page keeps its initial classrooms.
            selectedClassrooms: hasClassroomAssignments ? childData.classroomAssignments : initialClassroomsRef.current,
          });
        }

        setSelectedFamilies(normalizedFamilyIds);
        setPrimaryFamilyId(loadedPrimaryFamilyId);
        setFamilies(familiesData);
        setClassrooms(classroomsData);
        await fetchPrivacy(id);

        if (hasClassroomAssignments) {
          setSelectedClassrooms(childData.classroomAssignments);
        } else {
          console.warn("⚠️ classroomAssignments not found or invalid on child");
        }
      } catch (err) {
        console.error("❌ Error loading data:", err);
        setError(t("children.common.load_error"));
      } finally {
        setLoading(false);
      }
    };

    if (!keycloak?.token) return;
    if (dataLoadIdRef.current === id) return;
    dataLoadIdRef.current = id;
    fetchData();
  }, [keycloak?.token, id, t, mapChildToFormState, markClean]);

  useEffect(() => {
    if (!loading && !privacyLoading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading, privacyLoading]);

  // Client-side validations for lifecycle
  useEffect(() => {
    if (isFuture(child.withdrawnAt)) {
      setError(t("children.edit.err_future_withdrawn") || "Withdrawn date cannot be in the future.");
    } else if (isFuture(child.deceasedAt)) {
      setError(t("children.edit.err_future_deceased") || "Deceased date cannot be in the future.");
    } else if (child.withdrawnAt && child.deceasedAt) {
      setError(t("children.edit.err_mutually_exclusive") || "Withdrawn and deceased cannot both be set.");
    } else {
      setError(null);
    }
  }, [child.withdrawnAt, child.deceasedAt, t]);

  // Handlers
  const handleChange = (e) => {
    if (isInactive && !isLifecycleField(e.target.name)) return;
    formDirtyRef.current = true;
    setChild({ ...child, [e.target.name]: e.target.value });
  };

  const handleFamiliesChange = (newFamilies) => {
    if (isInactive) return;
    formDirtyRef.current = true;
    const normalized = (newFamilies || []).map((id) => String(id));
    setSelectedFamilies(normalized);
    setChild((prev) => ({ ...prev, families: normalized }));
  };

  const addFamily = (familyId) => {
    const normalizedId = String(familyId);
    if (!normalizedId || selectedFamilies.includes(normalizedId)) return;
    const nextFamilies = [...selectedFamilies, normalizedId];
    handleFamiliesChange(nextFamilies);
    if (!primaryFamilyId) setPrimaryFamilyId(normalizedId);
    setFamilySearchTerm("");
  };

  const removeFamily = (familyId) => {
    const normalizedId = String(familyId);
    const nextFamilies = selectedFamilies.filter((currentId) => currentId !== normalizedId);
    if (normalizedId === primaryFamilyId) {
      if (!nextFamilies.length) return;
      setPrimaryFamilyId(nextFamilies[0]);
    }
    handleFamiliesChange(nextFamilies);
  };

  const handleCloseReferenceModal = () => {
    setShowReferenceModal(false);
    setUnenrollReference(null);
    leaveForm(`/family/children/${id}/details`);
  };

  const copyReference = async () => {
    if (!unenrollReference) return;
    try {
      await navigator.clipboard.writeText(unenrollReference);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };

  const handleClassroomChange = (grade, selectedClassroomId) => {
    if (isInactive) return;
    formDirtyRef.current = true;
    const currentClassroomId = selectedClassrooms[grade];

    if (!selectedClassroomId && currentClassroomId) {
      setAssignmentsToDelete((prev) => [...new Set([...prev, currentClassroomId])]);
    } else if (selectedClassroomId && currentClassroomId && selectedClassroomId !== currentClassroomId) {
      setAssignmentsToDelete((prev) => prev.filter((id) => id !== currentClassroomId));
    }

    setSelectedClassrooms((prev) => ({
      ...prev,
      [grade]: selectedClassroomId || "",
    }));
  };

  const pruneEmpty = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== "" && v !== undefined));

  const extractReference = (data) => {
    if (!data) return null;
    if (typeof data === "string") {
      const match = data.match(/AVG-\d{4}-\d{5}/i);
      return match ? match[0] : null;
    }
    const stack = [data];
    const seen = new Set();
    while (stack.length) {
      const current = stack.pop();
      if (!current || typeof current !== "object") continue;
      if (seen.has(current)) continue;
      seen.add(current);
      if (typeof current.referenceId === "string" && current.referenceId.trim()) {
        return current.referenceId;
      }
      if (typeof current.reference === "string") {
        const ref = extractReference(current.reference);
        if (ref) return ref;
      }
      Object.values(current).forEach((v) => stack.push(v));
    }
    return null;
  };

  const handleSubmit = async (e, withoutBsnConfirmed = false) => {
    e?.preventDefault();
    if (error || saving) return;
    if (!String(originalChild?.bsn ?? "").trim() && !String(child.bsn ?? "").trim() && !withoutBsnConfirmed) {
      setConfirmWithoutBsn(true);
      return;
    }
    setSaving(true);

    try {
      // 🔹 Eerst privacy opslaan als er wijzigingen zijn
      if (privacyRef.current?.isDirty?.()) {
        await privacyRef.current.save();
      }

      // 🔹 Dan de child zelf
      const updatedFields = {};
      const fieldsToCheck = [
        "firstName",
        "lastName",
        "bsn",
        "gender",
        "dateBirth",
        "dateRegistration",
        "committed",
        "waiting",
      ];

      let nextAssignmentsToDelete = [...assignmentsToDelete];
      const localUpdated = { ...child };

      if (localUpdated.deceasedAt) {
        localUpdated.waiting = false;
        const current = Object.values(originalChild?.classroomAssignments || {}).filter(Boolean);
        current.forEach((classroomId) => {
          if (!nextAssignmentsToDelete.includes(classroomId)) {
            nextAssignmentsToDelete.push(classroomId);
          }
        });
      }

      fieldsToCheck.forEach((key) => {
        if (localUpdated[key] !== originalChild[key]) {
          updatedFields[key] = localUpdated[key];
        }
      });

      // lifecycle velden
      const lifecycleKeys = ["withdrawnAt", "deceasedAt"];
      lifecycleKeys.forEach((key) => {
        const newVal = localUpdated[key];
        const oldVal = originalChild[key] ?? null;

        if (newVal === "" || newVal === undefined) {
          if (oldVal !== null) {
            updatedFields[key] = null;
          }
        } else if (newVal !== oldVal) {
          updatedFields[key] = newVal;
        }
      });

      // classrooms diff
      const updatedAssignments = {};
      const originalAssignments = originalChild?.classroomAssignments || {};
      const keys = new Set([...Object.keys(selectedClassrooms), ...Object.keys(originalAssignments)]);
      for (const key of keys) {
        const newVal = selectedClassrooms[key] || null;
        const oldVal = originalAssignments[key] || null;
        if (newVal !== oldVal) updatedAssignments[key] = newVal;
      }

      setAssignmentsToDelete(nextAssignmentsToDelete);

      // PATCH child
      const payload = pruneEmpty(updatedFields);
      let newReference = null;
      if (Object.keys(payload).length > 0) {
        const res = await http.patch(`/children/${id}`, payload);
        // Noctis reference comes back in a response header when withdrawnAt triggers unenroll
        if (payload.withdrawnAt) {
          const headerRef =
            res?.headers?.["x-noctis-reference-id"] || res?.headers?.["X-Noctis-Reference-Id"];
          const bodyRef = extractReference(res?.data);
          newReference = headerRef || bodyRef;
        }
      }

      // Families sync
      const originalFamilyIds = originalChild.families?.map((f) => String(f.id)) || [];
      const originalPrimaryFamilyId = String(originalChild.familyId || originalChild.families?.[0]?.id || "");
      const primaryFamilyCreated = !originalPrimaryFamilyId && Boolean(primaryFamilyId);
      const primaryFamilyChanged = Boolean(originalPrimaryFamilyId) && Boolean(primaryFamilyId) && primaryFamilyId !== originalPrimaryFamilyId;
      if (primaryFamilyCreated) {
        await http.post(`/child-family`, null, { params: { childId: id, familyId: primaryFamilyId } });
      }
      if (primaryFamilyChanged) {
        await movePrimaryFamily(http, id, primaryFamilyId);
      }
      const familyIdsAfterPrimaryMove = primaryFamilyCreated
        ? [primaryFamilyId]
        : primaryFamilyChanged
        ? replacePrimaryFamily(originalFamilyIds, originalPrimaryFamilyId, primaryFamilyId)
        : originalFamilyIds;
      const currentFamilyIds = selectedFamilies.map((fid) => String(fid));
      const toAdd = currentFamilyIds.filter((fid) => !familyIdsAfterPrimaryMove.includes(fid));
      const toRemove = familyIdsAfterPrimaryMove.filter((fid) => !currentFamilyIds.includes(fid));

      // remove
      await Promise.all(
        toRemove.map((familyId) =>
          http.delete(`/child-family`, { params: { childId: id, familyId } })
        )
      );
      // add
      await Promise.all(
        toAdd.map((familyId) =>
          http.post(
            `/child-family`,
            null,
            { params: { childId: id, familyId } }
          )
        )
      );

      // Classroom unassign  ✅ via query-params i.p.v. JSON-body
      await Promise.all(
        nextAssignmentsToDelete.map((classroomId) =>
          http.post("/child-classrooms/unassign", null, {
            params: { childId: id, classroomId },
          })
        )
      );

      // Classroom assign
      const keyToLevelGrade = (key) => {
        const n = parseInt(String(key).replace(/\D/g, ""), 10);
        if (String(key).startsWith("kindergarten")) {
          return { level: "KINDERGARTEN", grade: n };
        }
        return { level: "GRADE", grade: n };
      };

      await Promise.all(
        Object.entries(updatedAssignments)
          .filter(([_, classroomId]) => classroomId)
          .map(([gradeKey, classroomId]) => {
            const { level, grade } = keyToLevelGrade(gradeKey);
            return http.patch(`/children/${id}/assign`, null, {
              params: { classroomId, level, grade },
            });
          })
      );

      if (newReference) {
        upsertCachedChild({
          ...originalChild,
          ...localUpdated,
          familyId: primaryFamilyId || null,
          families: families.filter((family) => currentFamilyIds.includes(String(family.id))),
          classroomAssignments: selectedClassrooms,
        });
        markClean({ child, selectedFamilies, primaryFamilyId, selectedClassrooms });
        setUnenrollReference(newReference);
        setShowReferenceModal(true);
      } else {
        upsertCachedChild({
          ...originalChild,
          ...localUpdated,
          familyId: primaryFamilyId || null,
          families: families.filter((family) => currentFamilyIds.includes(String(family.id))),
          classroomAssignments: selectedClassrooms,
        });
        markClean({ child, selectedFamilies, primaryFamilyId, selectedClassrooms });
        leaveWithoutWarning(() => leaveForm(`/family/children/${id}/details`));
      }
    } catch (err) {
      console.error("❌ Error during child update:", err);
      if (err.response) console.error("Response data:", err.response.data);
      setError(t("children.common.update_failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) {
    return <ErrorPage message={error} onRetry={() => window.location.reload()} />;
  }

  const isWithdrawn = Boolean(child.withdrawnAt);
  const isDeceased = Boolean(child.deceasedAt);
  const isInactive = isWithdrawn || isDeceased;

  const formatLifecycleDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("nl-NL");
  };

  const inactiveDate = isDeceased
    ? formatLifecycleDate(child.deceasedAt)
    : formatLifecycleDate(child.withdrawnAt);

  const inactiveFieldClasses = isInactive
    ? "bg-gray-200 text-gray-500 placeholder:text-gray-500 dark:bg-gray-800/80 dark:text-gray-400 cursor-not-allowed"
    : "";

  const ldsInputClasses = `h-12 w-full rounded-2xl border border-slate-200 bg-slate-50/70 px-4 text-sm font-semibold text-slate-950 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:border-indigo-400 dark:focus:bg-white/10 dark:focus:ring-indigo-400/10 ${inactiveFieldClasses}`;
  const ldsFieldCardClasses = "rounded-[18px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5";
  const ldsLabelClasses = "mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

  const lifecyclePanelClasses = isInactive
    ? "border border-rose-200/80 bg-gradient-to-br from-rose-50/85 via-white to-white shadow-[0_18px_50px_rgba(244,63,94,0.18)] dark:border-rose-500/50 dark:from-rose-950/30 dark:via-gray-900/50 dark:to-gray-900/60"
    : "border border-slate-200/80 bg-white shadow-sm dark:border-white/10 dark:bg-white/5";

  const formatYearRange = (classroom) => {
    const start = parseToDate(classroom?.startDate);
    const end = parseToDate(classroom?.endDate);
    const startYear = start?.getFullYear();
    const endYear = end?.getFullYear();
    if (!startYear || !endYear) return "";
    return `${startYear}/${endYear}`;
  };

  const tabs = [
    { id: "general", label: t("children.edit.general_tab") },
    { id: "classrooms", label: t("children.edit.classrooms_tab") },
    { id: "management", label: t("children.edit.management_tab", { defaultValue: "Beheer" }) },
    { id: "privacy", label: t("privacy.title", { defaultValue: "Privacy & Publishing" }) },
  ];

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return;
    const currentIndex = childEditTabOrder.indexOf(activeTab);
    const nextIndex = childEditTabOrder.indexOf(nextTab);
    setTabSlideDirection(nextIndex < currentIndex ? "back" : "forward");
    setActiveTab(nextTab);
  };

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-white sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={`/family/children/${id}/details`} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => leaveWithoutWarning(() => navigate(`/family/children/${id}/details`))}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
            >
              <FaTimes aria-hidden="true" />
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              form="child-edit-form"
              className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:text-white"
              disabled={saving}
            >
              <FaSave aria-hidden="true" />
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6">
        <header
          className={`relative overflow-hidden rounded-[24px] border p-5 shadow-[0_18px_45px_rgba(67,56,202,0.12)] sm:p-6 ${
            isInactive
              ? "border-rose-100/80 bg-gradient-to-r from-rose-50/90 via-white to-white dark:border-rose-400/25 dark:from-rose-950/35 dark:via-slate-950 dark:to-slate-950"
              : "border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/70 dark:border-indigo-400/25 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20"
          }`}
        >
          <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-center gap-5">
              <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-50 text-3xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100">
                {[child.firstName, child.lastName].filter(Boolean).map((part) => String(part).charAt(0).toUpperCase()).join("").slice(0, 2) || "?"}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
                  {t("children.edit.title", { defaultValue: "Edit child" })}
                </p>
                <h1 className="mt-1 min-w-0 break-words text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
                  {child.firstName} {child.lastName}
                </h1>
                <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center gap-2">
                    <FaBirthdayCake aria-hidden="true" className="text-violet-500" />
                    {child.dateBirth || t("common.not_available", { defaultValue: "Not available" })}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FaCalendarAlt aria-hidden="true" className="text-sky-500" />
                    {child.dateRegistration || t("common.not_available", { defaultValue: "Not available" })}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {isInactive ? (
                <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-100">
                  {isDeceased
                    ? t("children.details.deceased", { defaultValue: "Deceased" })
                    : t("children.details.withdrawn", { defaultValue: "Withdrawn" })}
                  {inactiveDate ? ` • ${inactiveDate}` : ""}
                </span>
              ) : (
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    child.waiting
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200"
                      : "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
                  }`}
                >
                  {child.waiting
                    ? t("children.edit.waiting-list")
                    : t("children.details.current", { defaultValue: "Active" })}
                </span>
              )}
              <span className='rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'>
                {child.committed
                  ? t("children.edit.committed_status", { defaultValue: "Committed" })
                  : t("children.edit.not_committed_status", { defaultValue: "Not committed" })}
              </span>
            </div>
          </div>

          {isInactive && (
            <div className="mt-3 rounded-2xl border border-rose-100/80 bg-rose-50/80 p-3 text-sm text-rose-800 shadow-[0_10px_35px_rgba(244,63,94,0.12)] dark:border-rose-500/50 dark:bg-rose-900/30 dark:text-rose-100">
              {isDeceased
                ? t("children.details.deceased_banner", {
                    defaultValue: "This child is deceased.",
                  })
                : t("children.details.withdrawn_banner", {
                    defaultValue: "This child has been withdrawn.",
                  })}
              {inactiveDate ? ` (${inactiveDate})` : ""}
            </div>
          )}
        </header>

        <div className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="grid gap-1.5 rounded-[24px] border border-slate-200/80 bg-white p-1.5 shadow-sm dark:border-white/10 dark:bg-white/5 sm:grid-cols-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`relative inline-flex min-h-[64px] items-center justify-center gap-3 rounded-[18px] px-4 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
                  activeTab === tab.id
                    ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                }`}
              >
                {tab.id === "general" && <FaChild aria-hidden="true" />}
                {tab.id === "classrooms" && <FaChalkboardTeacher aria-hidden="true" />}
                {tab.id === "management" && <FaCog aria-hidden="true" />}
                {tab.id === "privacy" && <FaShieldAlt aria-hidden="true" />}
                {tab.label}
                {activeTab === tab.id && <span className="absolute inset-x-5 bottom-1.5 h-1 rounded-full bg-indigo-600 dark:bg-indigo-300" />}
              </button>
            ))}
          </div>

        <form id="child-edit-form" onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div
            key={activeTab}
            className={`lumen-tab-slide lumen-tab-slide--${tabSlideDirection}`}
          >
          {/* ✅ General Info Tab */}
          {activeTab === "general" && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.first_name")}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={child.firstName}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  required
                  disabled={isInactive || saving}
                />
              </div>

              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.last_name")}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={child.lastName}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  required
                  disabled={isInactive || saving}
                />
              </div>

              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.bsn")}
                </label>
                <input
                  type="text"
                  name="bsn"
                  value={child.bsn ?? ""}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  disabled={isInactive || saving}
                />
              </div>

              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.gender")}
                </label>
                <select
                  name="gender"
                  value={child.gender}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  required
                  disabled={isInactive || saving}
                >
                  <option value="">{t("common.select")}</option>
                  <option value="M">{t("children.common.gender_male")}</option>
                  <option value="F">{t("children.common.gender_female")}</option>
                </select>
              </div>

              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.date_of_birth")}
                </label>
                <input
                  type="date"
                  name="dateBirth"
                  value={child.dateBirth}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  required
                  disabled={isInactive || saving}
                />
              </div>

              <div className={ldsFieldCardClasses}>
                <label className={ldsLabelClasses}>
                  {t("children.common.date_of_registration")}
                </label>
                <input
                  type="date"
                  name="dateRegistration"
                  value={child.dateRegistration}
                  onChange={handleChange}
                  className={ldsInputClasses}
                  required
                  disabled={isInactive || saving}
                />
              </div>

              <div className={`${ldsFieldCardClasses} lg:col-span-2`}>
                <div className="mb-4">
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">
                    {t("children.edit.linked_families", { defaultValue: "Families of this child" })}
                  </h2>
                  <p className="mt-1 flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <FaInfoCircle className="mt-0.5 shrink-0 text-indigo-500" aria-hidden="true" />
                    <span>{t("children.edit.primary_family_help", { defaultValue: "The primary family is the child's main family reference in Lumen. Changing it updates that reference; the child remains linked to the other families shown here." })}</span>
                  </p>
                </div>

                <div className="space-y-3">
                  {selectedFamilies.length ? selectedFamilies.map((familyId) => {
                    const family = families.find((candidate) => String(candidate.id) === familyId);
                    const parentNames = familyParentNames(family);
                    const isPrimary = familyId === primaryFamilyId;
                    const canRemove = selectedFamilies.length > 1;
                    return (
                      <div key={familyId} className={`rounded-2xl border p-4 transition ${isPrimary ? "border-indigo-300 bg-indigo-50/70 ring-2 ring-indigo-100 dark:border-indigo-400/60 dark:bg-indigo-400/10 dark:ring-indigo-400/10" : "border-slate-200 bg-slate-50/70 dark:border-white/10 dark:bg-white/5"}`}>
                        <div className="flex items-start justify-between gap-4">
                          <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                            <input
                              type="radio"
                              name="primaryFamily"
                              value={familyId}
                              checked={isPrimary}
                              onChange={() => { setPrimaryFamilyId(familyId); formDirtyRef.current = true; }}
                              disabled={isInactive || saving}
                              className="mt-1 h-4 w-4 accent-indigo-600"
                            />
                            <span className="min-w-0">
                              <span className="flex flex-wrap items-center gap-2 font-bold text-slate-900 dark:text-white">
                                {t("children.edit.family_name", { defaultValue: "Family {{name}}", name: family?.familyName || t("common.unknown", { defaultValue: "Unknown" }) })}
                                {isPrimary ? <span className="rounded-full bg-indigo-600 px-2.5 py-1 text-xs font-bold text-white">{t("children.edit.primary_family", { defaultValue: "Primary family" })}</span> : null}
                                {family?.active === false ? <span className="rounded-full bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200">{t("common.inactive", { defaultValue: "Inactive" })}</span> : null}
                              </span>
                              <span className="mt-1 block text-sm text-slate-600 dark:text-slate-300">
                                {parentNames.length ? parentNames.join(" · ") : t("children.edit.no_parents", { defaultValue: "No parents or guardians linked" })}
                              </span>
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={() => removeFamily(familyId)}
                            disabled={isInactive || saving || !canRemove}
                            title={!canRemove ? t("children.edit.last_family_remove_help", { defaultValue: "Link another family before removing the only family." }) : undefined}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-200 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-400/30 dark:hover:bg-red-400/10"
                            aria-label={t("children.edit.remove_family", { defaultValue: "Remove family {{name}}", name: family?.familyName || "" })}
                          >
                            <FaTimes aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-white/5 dark:text-slate-300">
                      {t("children.edit.no_linked_families", { defaultValue: "This child is not linked to a family yet." })}
                    </div>
                  )}
                </div>

                <div className="mt-5 border-t border-slate-200 pt-5 dark:border-white/10">
                  <label htmlFor="child-family-search" className={ldsLabelClasses}>
                    {t("children.edit.add_family", { firstName: child.firstName || "", defaultValue: "Add {{firstName}} to a family" })}
                  </label>
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <input
                      id="child-family-search"
                      type="search"
                      value={familySearchTerm}
                      onChange={(event) => setFamilySearchTerm(event.target.value)}
                      placeholder={t("children.edit.family_search_placeholder", { defaultValue: "Search by family, parent or email..." })}
                      className={`${ldsInputClasses} pl-11`}
                      disabled={isInactive || saving}
                    />
                  </div>
                  {familySearchTerm.trim() ? (
                    <div className="mt-2 max-h-64 space-y-2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                      {families
                        .filter((family) => !selectedFamilies.includes(String(family.id)))
                        .filter((family) => familySearchText(family).includes(familySearchTerm.trim().toLocaleLowerCase()))
                        .slice(0, 20)
                        .map((family) => {
                          const parentNames = familyParentNames(family);
                          return (
                            <button key={family.id} type="button" onClick={() => addFamily(family.id)} className="flex w-full items-center justify-between gap-4 rounded-xl px-3 py-3 text-left transition hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none dark:hover:bg-indigo-400/10 dark:focus:bg-indigo-400/10">
                              <span className="min-w-0">
                                <span className="block font-bold text-slate-900 dark:text-white">{t("children.edit.family_name", { defaultValue: "Family {{name}}", name: family.familyName })}</span>
                                <span className="mt-0.5 block text-sm text-slate-600 dark:text-slate-300">{t("children.edit.parents_label", { defaultValue: "Parents/guardians: {{names}}", names: parentNames.length ? parentNames.join(", ") : t("children.edit.no_parents", { defaultValue: "None linked" }) })}</span>
                              </span>
                              <span className="shrink-0 text-sm font-bold text-indigo-600 dark:text-indigo-300">{t("common.actions.add", { defaultValue: "Add" })}</span>
                            </button>
                          );
                        })}
                      {!families.some((family) => !selectedFamilies.includes(String(family.id)) && familySearchText(family).includes(familySearchTerm.trim().toLocaleLowerCase())) ? (
                        <p className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">{t("children.edit.no_family_results", { defaultValue: "No matching families found." })}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={`${ldsFieldCardClasses} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-2`}>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  {t("children.common.committed")}
                </label>
                <Switch
	                  checked={child.committed ?? false}
	                  onChange={(value) => {
	                    formDirtyRef.current = true;
	                    setChild({ ...child, committed: value });
	                  }}
                  disabled={isInactive || saving}
                  className={`${
                    child.committed ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition ${isInactive || saving ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`${
                      child.committed ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
	                    checked={!!child.waiting}
	                    onChange={(e) => {
	                      formDirtyRef.current = true;
	                      setChild({ ...child, waiting: e.target.checked });
	                    }}
                    className="h-5 w-5 accent-indigo-600"
                    disabled={isInactive || saving}
                  />
                  <span>{t("children.edit.waiting-list")}</span>
                </label>
              </div>
            </div>
          )}

          {activeTab === "management" && (
            <div className={`rounded-[24px] p-5 ${lifecyclePanelClasses}`}>
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                  <FaCog aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    {t("children.edit.management_tab", { defaultValue: "Beheer" })}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                    {t("children.edit.lifecycle_help", {
                      defaultValue:
                        "These fields control retention and access. Use only when the student has left or is deceased.",
                    })}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className={ldsFieldCardClasses}>
                  <label className={ldsLabelClasses}>
                    {t("children.edit.withdrawn_at", {
                      defaultValue: "Withdrawn at",
                    })}
                  </label>
                  <input
                    type="date"
                    name="withdrawnAt"
                    value={child.withdrawnAt || ""}
                    onChange={handleChange}
                    disabled={!!child.deceasedAt || saving}
                    className={`${ldsInputClasses} ${saving ? "opacity-80" : ""}`}
                  />
                </div>

                <div className={ldsFieldCardClasses}>
                  <label className={ldsLabelClasses}>
                    {t("children.edit.deceased_at", {
                      defaultValue: "Deceased at",
                    })}
                  </label>
                  <input
                    type="date"
                    name="deceasedAt"
                    value={child.deceasedAt || ""}
                    onChange={handleChange}
                    disabled={!!child.withdrawnAt || saving}
                    className={`${ldsInputClasses} ${saving ? "opacity-80" : ""}`}
                  />
                </div>

                {child.withdrawnAt && !child.deceasedAt && (
                  <label className={`${ldsFieldCardClasses} flex items-center gap-3 text-sm font-semibold text-slate-700 dark:text-slate-200 lg:col-span-2`}>
                    <input
                      type="checkbox"
                      checked={assignmentsToDelete.length > 0}
                      onChange={(e) => {
                        formDirtyRef.current = true;
                        if (e.target.checked) {
                          const current = Object.values(
                            originalChild?.classroomAssignments || {}
                          ).filter(Boolean);
                          setAssignmentsToDelete(current);
                        } else {
                          setAssignmentsToDelete([]);
                        }
                      }}
                      disabled={isInactive || saving}
                      className="h-5 w-5 accent-indigo-600"
                    />
                    <span>
                      {t("children.edit.unassign_on_withdrawn", {
                        defaultValue: "Unassign current classes now",
                      })}
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Classrooms Tab */}
          {activeTab === "classrooms" && (
            <div className="rounded-[24px] border border-slate-200/80 bg-white p-5 text-slate-900 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
              {/* Primary Section Tabs */}
              <div className="mb-5 grid gap-2 rounded-[18px] bg-slate-100/80 p-1.5 dark:bg-white/5 sm:grid-cols-4">
                {["kindergarten", "lower", "middle", "upper"].map((section) => (
                  <button
                    key={section}
                    type="button"
                    className={`rounded-[14px] px-4 py-2.5 text-center text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      activeSection === section
                        ? "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20"
                        : "text-slate-500 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                    }`}
                    onClick={() => setActiveSection(section)}
                  >
                    {t(`children.edit.${section}_grades`)}
                  </button>
                ))}
              </div>

              {/* Classroom Selection */}
              <div className="grid gap-4 md:grid-cols-2">
                {activeSection === "kindergarten" && (
                  <>
                    {getKindergartenKeys().map((kgKey, index) => (
                      <div key={kgKey} className={ldsFieldCardClasses}>
                        <label className={ldsLabelClasses}>
                          {translateClassName("Kindergarten")} {t("common.years")} {index + 1}
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedClassrooms[kgKey] || ""}
                            onChange={(e) => handleClassroomChange(kgKey, e.target.value)}
                            disabled={isInactive || saving}
                            className={ldsInputClasses}
                          >
                            <option value="">{t("common.select")}</option>
                            {classrooms
                              .filter((c) => c.classType === "KINDERGARTEN")
                              .sort((a, b) => {
                                const aDate = parseToDate(a.startDate);
                                const bDate = parseToDate(b.startDate);
                                if (aDate && bDate) return aDate - bDate;
                                if (!aDate && !bDate) return 0;
                                return aDate ? -1 : 1;
                              })
                              .map((c) => {
                                const year = formatYearRange(c);
                                return (
                                  <option key={c.id} value={c.id}>
                                      {translateClassName(c.className)} - {year}
                                  </option>
                                );
                              })}
                          </select>
                          {selectedClassrooms[kgKey] && (
                            <button
                              type="button"
                              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/25 dark:bg-white/5 dark:text-red-200"
                              onClick={() => handleClassroomChange(kgKey, null)}
                              disabled={isInactive || saving}
                              aria-label={t("common.actions.remove", { defaultValue: "Remove" })}
                            >
                              <FaTimes aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {activeSection === "lower" && (
                  <>
                    {["grade1", "grade2", "grade3", "grade4", "grade5", "grade6"].map(
                      (grade, index) => (
                        <div key={grade} className={ldsFieldCardClasses}>
                          <label className={ldsLabelClasses}>
                            {t(`classrooms.common.grade`)} {index + 1}
                          </label>
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedClassrooms[grade] || ""}
                              onChange={(e) => handleClassroomChange(grade, e.target.value)}
                              disabled={isInactive || saving}
                              className={ldsInputClasses}
                            >
                              <option value="">{t("common.select")}</option>
                              {classrooms
                                .filter(
                                  (classroom) =>
                                    classroom.classType === "GRADE" &&
                                    Number(classroom.grade) === index + 1
                                )
                                .map((classroom) => (
                                  <option key={classroom.id} value={classroom.id}>
                                    {translateClassName(classroom.className)} - {formatYearRange(classroom)}
                                  </option>
                                ))}
                            </select>
                            {selectedClassrooms[grade] && (
                              <button
                                type="button"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/25 dark:bg-white/5 dark:text-red-200"
                                onClick={() => handleClassroomChange(grade, null)}
                                disabled={isInactive || saving}
                                aria-label={t("common.actions.remove", { defaultValue: "Remove" })}
                              >
                                <FaTimes aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </>
                )}

                {activeSection === "middle" && (
                  <>
                    {["grade7", "grade8"].map((grade, index) => (
                      <div key={grade} className={ldsFieldCardClasses}>
                        <label className={ldsLabelClasses}>
                          {t(`classrooms.common.grade`)} {index + 7}
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedClassrooms[grade] || ""}
                            onChange={(e) => handleClassroomChange(grade, e.target.value)}
                            disabled={isInactive || saving}
                            className={ldsInputClasses}
                          >
                            <option value="">{t("common.select")}</option>
                              {classrooms
                                .filter(
                                  (classroom) =>
                                    classroom.classType === "GRADE" &&
                                    Number(classroom.grade) === index + 7
                                )
                                .map((classroom) => (
                                  <option key={classroom.id} value={classroom.id}>
                                    {translateClassName(classroom.className)} - {formatYearRange(classroom)}
                                  </option>
                                ))}
                            </select>
                            {selectedClassrooms[grade] && (
                              <button
                              type="button"
                              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/25 dark:bg-white/5 dark:text-red-200"
                              onClick={() => handleClassroomChange(grade, null)}
                              disabled={isInactive || saving}
                              aria-label={t("common.actions.remove", { defaultValue: "Remove" })}
                            >
                              <FaTimes aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {activeSection === "upper" && (
                  <>
                    {["grade9", "grade10", "grade11", "grade12"].map((grade, index) => (
                      <div key={grade} className={ldsFieldCardClasses}>
                        <label className={ldsLabelClasses}>
                          {t(`classrooms.common.grade`)} {index + 9}
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedClassrooms[grade] || ""}
                            onChange={(e) => handleClassroomChange(grade, e.target.value)}
                            disabled={isInactive || saving}
                            className={ldsInputClasses}
                          >
                            <option value="">{t("common.select")}</option>
                              {classrooms
                                .filter(
                                  (classroom) =>
                                    classroom.classType === "GRADE" &&
                                    Number(classroom.grade) === index + 9
                                )
                                .map((classroom) => (
                                  <option key={classroom.id} value={classroom.id}>
                                    {translateClassName(classroom.className)} - {formatYearRange(classroom)}
                                  </option>
                                ))}
                            </select>
                            {selectedClassrooms[grade] && (
                              <button
                              type="button"
                              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/25 dark:bg-white/5 dark:text-red-200"
                              onClick={() => handleClassroomChange(grade, null)}
                              disabled={isInactive || saving}
                              aria-label={t("common.actions.remove", { defaultValue: "Remove" })}
                            >
                              <FaTimes aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 🔵 Privacy Tab */}
          {activeTab === "privacy" && (
            <div className="p-0">
              {privacyLoading ? (
                <Spinner />
              ) : privacyError ? (
                <ErrorPage message={privacyError} onRetry={() => fetchPrivacy(id)} />
              ) : (
                <ChildPrivacyForm
                  ref={privacyRef}
                  onDirtyChange={setPrivacyDirty}
                  value={privacy}
                  onSave={savePrivacy}
                  disabled={saving || isInactive}
                />
              )}
            </div>
          )}
          </div>

          {/* ✅ Action Buttons — nu ALTIJD zichtbaar (ook op privacy-tab) */}
	          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
	            <button
	              type="button"
	              onClick={() => leaveWithoutWarning(() => navigate(`/family/children/${id}/details`))}
	              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
	            >
	              <FaTimes aria-hidden="true" />
	              {t("common.cancel")}
	            </button>
	            <button
	              type="submit"
	              className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
	              disabled={saving}
	            >
	              <FaSave aria-hidden="true" />
	              {saving ? t("common.saving") : t("common.save")}
	            </button>
	          </div>
	        </form>
	      </div>
	    </div>
      </SlidePageTransition>
      {showReferenceModal && (
        <Modal
          show={showReferenceModal}
          title={t("children.edit.withdraw_reference_title", {
            defaultValue: "Withdrawal submitted",
          })}
          message={
            <div className="space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {t("children.edit.withdraw_reference_body", {
                  defaultValue:
                    "We sent the unenrollment to Noctis. Keep this reference (e.g. AVG-12345); it is not stored in Lumen.",
                })}
              </p>
              {unenrollReference && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={unenrollReference}
                    className="w-full rounded border px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800"
                  />
                  <button
                    type="button"
                    onClick={copyReference}
                    className="whitespace-nowrap rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/60 dark:bg-indigo-950/40 dark:text-indigo-200"
                  >
                    {t("children.edit.withdraw_reference_copy", { defaultValue: "Copy" })}
                  </button>
                </div>
              )}
            </div>
          }
          isConfirm={true}
          type="info"
          confirmText={t("common.actions.close")}
          onConfirm={handleCloseReferenceModal}
          onClose={handleCloseReferenceModal}
        />
      )}
      <Modal
        show={confirmWithoutBsn}
        type="confirm"
        variant="lds"
        dialogClassName="max-w-lg"
        title={t("children.edit.save_without_bsn_title", { defaultValue: "Save without BSN?" })}
        message={t("children.edit.save_without_bsn_message", {
          defaultValue: "The BSN will remain empty. The parent will continue to receive the prompt to provide it. Save the other changes?",
        })}
        onClose={() => setConfirmWithoutBsn(false)}
        footer={(
          <div className="flex justify-end gap-3">
            <button type="button" className="rounded-lg border px-4 py-2" onClick={() => setConfirmWithoutBsn(false)}>
              {t("children.edit.save_without_bsn_back", { defaultValue: "Back" })}
            </button>
            <button type="button" disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
              onClick={() => { setConfirmWithoutBsn(false); handleSubmit(null, true); }}>
              {t("children.edit.save_without_bsn_confirm", { defaultValue: "Save without BSN" })}
            </button>
          </div>
        )}
      />
    </PageTemplate>
  );
};

export default ChildEdit;
